/**
 * EVENTSFLO-SETTINGS-DELETE-EMERGENT-TEST-DATA-TOOL-V1
 *
 * Settings → Delete Emergent Test Data
 *
 * Two-step workflow:
 *   1. Click "Run preview" → dry-run scan, no DB writes.
 *   2. Review the grouped list (safe / blocked / ambiguous).
 *   3. Tick the "I understand" checkbox + click "Delete selected TEST records".
 *
 * Backend enforces:
 *   - tenant scoping
 *   - re-classification at delete time (refuses stale plans)
 *   - blocked / ambiguous rows are never deleted, ever.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Eraser,
  Loader2,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import api, { extractErrorMessage } from "../lib/api";

export default function TestDataCleanupPage() {
  const navigate = useNavigate();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ledger, setLedger] = useState(null);

  const runPreview = async () => {
    setPreviewLoading(true);
    setLedger(null);
    try {
      const { data } = await api.get("/admin/test-data-cleanup/preview");
      setPlan(data);
      const total = (data?.totals?.safe || 0) + (data?.totals?.blocked || 0) + (data?.totals?.ambiguous || 0);
      if (total === 0) {
        toast.success("No Emergent/test data found in this tenant.");
      } else {
        toast.success(
          `Found ${data.totals.safe} safe · ${data.totals.blocked} blocked · ${data.totals.ambiguous} ambiguous record(s).`
        );
      }
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runDelete = async () => {
    if (!plan) return;
    if (!ack) {
      toast.error("Please acknowledge the warning before deleting.");
      return;
    }
    setDeleteLoading(true);
    try {
      const { data } = await api.post("/admin/test-data-cleanup/delete", {
        plan,
        confirmed: true,
      });
      setLedger(data);
      const deleted = Object.values(data?.deleted_counts || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `Deleted ${deleted} record(s). Skipped: ${data?.skipped?.length || 0}. Errors: ${data?.errors?.length || 0}.`
      );
      setConfirmOpen(false);
      setAck(false);
      // Re-run preview so the page reflects the new state.
      await runPreview();
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const totals = plan?.totals || { safe: 0, blocked: 0, ambiguous: 0 };
  const hasSafe = (totals.safe || 0) > 0;

  return (
    <div className="container mx-auto p-6 max-w-5xl space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/settings")}
          data-testid="back-to-settings-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Settings
        </Button>
      </div>

      <Card className="border-amber-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eraser className="h-5 w-5 text-amber-600" />
            Delete Emergent Test Data
          </CardTitle>
          <CardDescription>
            Scan this tenant for records visibly marked <code className="font-mono">TEST</code> or carrying a structured
            test marker (created during verification) and remove them in one safe step. Records without a visible
            marker are never deleted automatically — they're surfaced as ambiguous so you can review them manually.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-amber-300/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs flex gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-amber-900 dark:text-amber-200 space-y-1">
              <div>This tool is tenant-scoped — it will only touch records belonging to your current tenant.</div>
              <div>Blocked records (still referenced by real, non-TEST production data) are skipped, never orphaned.</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={runPreview}
              disabled={previewLoading}
              data-testid="test-data-cleanup-preview-btn"
            >
              {previewLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</>
              ) : (
                "Run preview"
              )}
            </Button>
            {plan && (
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="default" data-testid="test-data-cleanup-safe-count">Safe: {totals.safe}</Badge>
                <Badge variant="destructive" data-testid="test-data-cleanup-blocked-count">Blocked: {totals.blocked}</Badge>
                <Badge variant="secondary" data-testid="test-data-cleanup-ambiguous-count">Ambiguous: {totals.ambiguous}</Badge>
              </div>
            )}
          </div>

          {plan && (
            <div className="space-y-4" data-testid="test-data-cleanup-results">
              {plan.groups?.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No TEST records found in this tenant.
                </div>
              ) : (
                plan.groups.map((grp) => (
                  <div key={grp.collection} className="border rounded-md">
                    <div className="px-3 py-2 bg-muted/40 border-b flex items-center justify-between">
                      <div className="font-medium text-sm">{grp.label}</div>
                      <div className="flex gap-2 text-[10px]">
                        <Badge variant="outline">safe {grp.safe_count}</Badge>
                        <Badge variant="outline">blocked {grp.blocked_count}</Badge>
                        <Badge variant="outline">ambiguous {grp.ambiguous_count}</Badge>
                      </div>
                    </div>
                    <ul className="divide-y text-xs">
                      {grp.records.map((rec) => (
                        <li key={rec.id} className="px-3 py-2 flex items-start gap-2">
                          {rec.blocked ? (
                            <Badge variant="destructive">blocked</Badge>
                          ) : rec.ambiguous ? (
                            <Badge variant="secondary">ambiguous</Badge>
                          ) : (
                            <Badge>safe</Badge>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{rec.name}</div>
                            <div className="text-muted-foreground">id: <code className="text-[10px]">{rec.id}</code></div>
                            <div className="text-muted-foreground">{rec.reason}</div>
                            {rec.blocked_reason && (
                              <div className="text-destructive">⚠ {rec.blocked_reason}</div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}

              {hasSafe && (
                <div className="pt-2">
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmOpen(true)}
                    data-testid="test-data-cleanup-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete selected TEST records ({totals.safe})
                  </Button>
                </div>
              )}
            </div>
          )}

          {ledger && (
            <Card className="border-emerald-300/40" data-testid="test-data-cleanup-ledger">
              <CardHeader>
                <CardTitle className="text-base">Deletion ledger</CardTitle>
                <CardDescription>{ledger.executed_at}</CardDescription>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <div>
                  <div className="font-medium mb-1">Deleted</div>
                  {Object.keys(ledger.deleted_counts || {}).length === 0 ? (
                    <div className="text-muted-foreground">Nothing deleted.</div>
                  ) : (
                    <ul className="list-disc list-inside">
                      {Object.entries(ledger.deleted_counts).map(([coll, n]) => (
                        <li key={coll}><code>{coll}</code> × {n}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {(ledger.skipped?.length || 0) > 0 && (
                  <div>
                    <div className="font-medium mb-1">Skipped</div>
                    <ul className="list-disc list-inside text-muted-foreground">
                      {ledger.skipped.map((s, i) => (
                        <li key={i}><code>{s.collection}</code> id={s.id} — {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(ledger.errors?.length || 0) > 0 && (
                  <div>
                    <div className="font-medium mb-1 text-destructive">Errors</div>
                    <ul className="list-disc list-inside text-destructive">
                      {ledger.errors.map((s, i) => (
                        <li key={i}><code>{s.collection}</code> id={s.id} — {s.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(v) => { if (!deleteLoading) { setConfirmOpen(v); if (!v) setAck(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirm test-data deletion
            </DialogTitle>
            <DialogDescription>
              This will permanently delete records identified as Emergent/test data. Review the list before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2 py-2">
            <div>You're about to delete <span className="font-semibold">{totals.safe}</span> safe record(s).</div>
            <div className="text-xs text-muted-foreground">
              Blocked ({totals.blocked}) and ambiguous ({totals.ambiguous}) records will be skipped.
            </div>
            <label className="flex items-start gap-2 mt-3 cursor-pointer text-sm">
              <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="test-data-cleanup-ack" />
              <span>I understand this will delete the listed TEST records.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={deleteLoading} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!ack || deleteLoading}
              onClick={runDelete}
              data-testid="test-data-cleanup-confirm-delete-btn"
            >
              {deleteLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting…</>
              ) : (
                <>Delete {totals.safe} record(s)</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
